/**
 * useEntitlements / useEntitlement — Phase 3 (web), docs/plans/TIER_GATING_PLAN.md §B.1.
 *
 * Mirrors the `useCompany` pattern (src/hooks/useCompany.ts): one shared React
 * Query cache entry (`ENTITLEMENTS_QUERY_KEY`) so every caller — the sidebar
 * menu, UpgradeGate, the Plan & Billing page — shares a single network fetch.
 *
 * FAIL-OPEN (NON-NEGOTIABLE, matches shared/entitlements.ts hasFeature()):
 * while the query is loading OR has errored, `entitlements` is `undefined`,
 * and `hasFeature(undefined, key)` returns `true`. A slow/failed
 * `/api/entitlements` fetch must never lock a paying tenant out of their own
 * data or flash a lock badge before the real value arrives.
 *
 * Refetch policy: the app's global QueryClient default is
 * `refetchOnWindowFocus: false` (QueryProvider.tsx — avoids refetch storms on
 * Supabase WS reconnects), so this hook opts back in explicitly with a 60s
 * staleTime — React Query only refetches on focus when the cached data is
 * stale, so a focus within 60s of the last fetch is a no-op.
 *
 * `refreshEntitlements()` is a plain (non-hook) export for callers outside a
 * component (e.g. after a future purchase confirmation) — it dispatches a
 * window event that every mounted `useEntitlements()` observer picks up and
 * turns into `queryClient.invalidateQueries()`, the same custom-event pattern
 * `src/lib/api.ts` already uses for `jm:accounting-reauth` / `jm:idle-warning`.
 */
'use client'

import { useEffect } from 'react'

import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'

import {
  hasFeature,
  minimumPlanFor,
  type Entitlements,
  type FeatureKey,
} from '@shared/entitlements'

import { api } from '@/lib/api'
import { useOptionalAuth } from '@/context/AuthContext'

export const ENTITLEMENTS_QUERY_KEY = ['entitlements'] as const

/** 60s — throttles the window-focus refetch (see module doc comment above). */
export const ENTITLEMENTS_STALE_TIME = 60 * 1000
export const ENTITLEMENTS_GC_TIME = 10 * 60 * 1000

export const ENTITLEMENTS_REFRESH_EVENT = 'jm:entitlements-refresh'

/** Plain query-options object for non-hook callers (`queryClient.fetchQuery(entitlementsQueryOptions)`). */
export const entitlementsQueryOptions = {
  queryKey: ENTITLEMENTS_QUERY_KEY,
  queryFn: () => api.get<Entitlements>('/api/entitlements'),
  staleTime: ENTITLEMENTS_STALE_TIME,
  gcTime: ENTITLEMENTS_GC_TIME,
}

/**
 * Ask any mounted `useEntitlements()` observer to invalidate + refetch — call
 * this right after a future purchase/upgrade confirmation so the app sees the
 * new plan immediately instead of waiting out the 60s staleTime. Safe to call
 * with no observers mounted (no-op).
 */
export function refreshEntitlements() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(ENTITLEMENTS_REFRESH_EVENT))
}

/**
 * The current tenant's resolved entitlements. `enabled` is gated on
 * `isAuthenticated` — logged-out users never issue this request.
 */
export function useEntitlements(
  options?: Partial<Omit<UseQueryOptions<Entitlements>, 'queryKey' | 'staleTime' | 'gcTime'>>
) {
  const auth = useOptionalAuth()
  const queryClient = useQueryClient()
  const isAuthenticated = !!auth?.user

  const query = useQuery<Entitlements>({
    queryFn: () => api.get<Entitlements>('/api/entitlements'),
    refetchOnWindowFocus: true,
    ...options,
    queryKey: ENTITLEMENTS_QUERY_KEY,
    staleTime: ENTITLEMENTS_STALE_TIME,
    gcTime: ENTITLEMENTS_GC_TIME,
    enabled: (options?.enabled ?? true) && isAuthenticated,
  })

  // Cross-window/cross-component refresh signal — see refreshEntitlements() above.
  useEffect(() => {
    const handler = () => { queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY }) }

    window.addEventListener(ENTITLEMENTS_REFRESH_EVENT, handler)
    
return () => window.removeEventListener(ENTITLEMENTS_REFRESH_EVENT, handler)
  }, [queryClient])

  return query
}

export type UseEntitlementResult = {

  /** Fail-open: true while loading, on error, or when the feature is included. */
  allowed: boolean
  minimumPlan: ReturnType<typeof minimumPlanFor>
  loading: boolean
  entitlements: Entitlements | undefined
}

/** Answers "does this tenant's plan include `key`?" — fail-open by design (see module doc comment). */
export function useEntitlement(key: FeatureKey): UseEntitlementResult {
  const { data, isLoading } = useEntitlements()

  
return {
    allowed: hasFeature(data, key),
    minimumPlan: minimumPlanFor(key),
    loading: isLoading,
    entitlements: data,
  }
}
